// CORRECTION COMPLÈTE de useAuth pour éviter la boucle infinie
// src/hooks/useAuth.tsx
import { LoadingSpinner } from '../components/ui/LoadingSpinner'; // Ajoutez cette ligne
import { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { supabase } from '@/utils/supabase';
import { useLocalStorage } from './useLocalStorage';
import { analytics } from '@/utils/analytics';
import { metaPixel } from '@/utils/meta-pixel';

interface User {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  user_type?: string;
  status?: string;
  credits?: number;
  subscription_id?: string;
  subscription_status?: string;
  subscription_price_id?: string;
  company_name?: string;
  company_logo_url?: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, firstName?: string, lastName?: string) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<boolean>;
  refreshUserProfile: () => Promise<void>;
  session: any | null;
  refreshUserCredits: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useLocalStorage<User | null>('dpe_user', null);
  const [isLoading, setIsLoading] = useState(true);
  const [isCheckingAuth, setIsCheckingAuth] = useState(false); // Nouveau flag pour éviter les appels multiples

  // Fonction utilitaire pour récupérer le profil directement depuis Supabase
  const fetchProfileFromSupabase = async (userId: string): Promise<Partial<User>> => {
    try {
      console.log('🔍 DEBUG fetchProfile: Fetching profile for user:', userId);

      const { data: profile, error } = await supabase
        .from('profiles')
        .select('id, email, first_name, last_name, credits, subscription_id, subscription_status, subscription_price_id, company_name, company_logo_url')
        .eq('id', userId)
        .single();

      if (error) {
        console.error('❌ DEBUG fetchProfile: Error fetching profile from Supabase:', error);
        throw error;
      }

      if (profile) {
        console.log('✅ DEBUG fetchProfile: Profile fetched successfully from Supabase');
        console.log('🔍 DEBUG fetchProfile: Raw profile data:', profile);

        return {
          credits: profile.credits || 0,
          subscription_id: profile.subscription_id || undefined,
          subscription_status: profile.subscription_status || 'inactive',
          subscription_price_id: profile.subscription_price_id || undefined,
          company_name: profile.company_name || undefined,
          company_logo_url: profile.company_logo_url || undefined
        };
      }
    } catch (error) {
      console.error('❌ DEBUG fetchProfile: Error fetching profile:', error);
    }

    // Retourner des valeurs par défaut en cas d'erreur
    return {
      credits: 0,
      subscription_status: 'inactive'
    };
  };

  // Login function
  const login = async (email: string, password: string) => {
    try {
      console.log('🔍 DEBUG login: Starting login process for:', email);
      setIsLoading(true);
      
      // Authentification via API REST Supabase
      const response = await fetch(`${supabase.supabaseUrl}/auth/v1/token?grant_type=password`, {
        method: 'POST',
        headers: {
          'apikey': supabase.supabaseKey,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email,
          password
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error_description || errorData.message || 'Email ou mot de passe incorrect');
      }
      
      const authData = await response.json();
      console.log('✅ DEBUG login: Authentication successful for user:', authData.user?.id);
      
      if (!authData.user || !authData.access_token) {
        throw new Error('Données d\'authentification invalides');
      }
      
      // Construction des données utilisateur de base
      const userData: User = {
        id: authData.user.id,
        email: authData.user.email || '',
        first_name: authData.user.user_metadata?.first_name || '',
        last_name: authData.user.user_metadata?.last_name || '',
        user_type: 'individual',
        status: 'active',
        credits: 0,
        subscription_status: 'inactive'
      };
      
      // Récupération du profil
      const profileData = await fetchProfileFromSupabase(authData.user.id);
      
      // Fusion des données
      const completeUserData = { ...userData, ...profileData };
      console.log('✅ DEBUG login: Setting complete user data:', completeUserData);
      
      // Définition de la session Supabase pour les futures requêtes
      try {
        await supabase.auth.setSession({
          access_token: authData.access_token,
          refresh_token: authData.refresh_token
        });
        console.log('✅ DEBUG login: Supabase session set successfully');
      } catch (sessionError) {
        console.warn('⚠️ DEBUG login: Session setting failed (continuing anyway):', sessionError);
      }
      
      setUser(completeUserData);
      
      // Analytics
      analytics.trackLogin(authData.user.id, 'email');
      metaPixel.trackLogin();
      
      console.log('✅ DEBUG login: Login process completed successfully');
      
    } catch (error: any) {
      console.error('❌ DEBUG login: Login failed:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Check auth function - SIMPLIFIÉE pour éviter les boucles
  const checkAuth = useCallback(async () => {
    // Éviter les appels multiples simultanés
    if (isCheckingAuth) {
      console.log('🔍 DEBUG checkAuth: Already checking auth, skipping...');
      return !!user;
    }
    
    try {
      console.log('🔍 DEBUG checkAuth: Starting authentication check...');
      setIsCheckingAuth(true);
      
      const { data } = await supabase.auth.getSession();
      const currentSession = data.session;
      
      console.log('🔍 DEBUG checkAuth: Session check result:', {
        hasSession: !!currentSession,
        userId: currentSession?.user?.id
      });
      
      if (!currentSession) {
        console.log('🔍 DEBUG checkAuth: No session found, clearing user');
        setUser(null);
        return false;
      }
      
      // Si on a déjà un utilisateur avec le même ID, pas besoin de refetch
      if (user && user.id === currentSession.user.id) {
        console.log('🔍 DEBUG checkAuth: User already loaded, skipping profile fetch');
        return true;
      }
      
      // Construction des données utilisateur de base
      const userData: User = {
        id: currentSession.user.id,
        email: currentSession.user.email || '',
        first_name: currentSession.user.user_metadata?.first_name || '',
        last_name: currentSession.user.user_metadata?.last_name || '',
        user_type: 'individual',
        status: 'active',
        credits: 0,
        subscription_status: 'inactive'
      };
      
      // Récupération du profil (uniquement si pas déjà chargé)
      const profileData = await fetchProfileFromSupabase(currentSession.user.id);
      const completeUserData = { ...userData, ...profileData };
      
      console.log('✅ DEBUG checkAuth: Setting user data from session check');
      setUser(completeUserData);
      return true;
      
    } catch (error) {
      console.error('❌ DEBUG checkAuth: Error during auth check:', error);
      setUser(null);
      return false;
    } finally {
      setIsCheckingAuth(false);
      setIsLoading(false);
    }
  }, [isCheckingAuth, user, setUser]);

  // Register function
  const register = async (email: string, password: string, firstName?: string, lastName?: string) => {
    try {
      setIsLoading(true);

      const response = await fetch('/.netlify/functions/auth-register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          password,
          first_name: firstName || '',
          last_name: lastName || '',
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || result.error || 'Erreur lors de la création du compte');
      }

      if (!result.user || !result.session?.access_token) {
        throw new Error('Données d\'inscription invalides');
      }

      // Establish Supabase session so RLS queries work immediately
      try {
        await supabase.auth.setSession({
          access_token: result.session.access_token,
          refresh_token: result.session.refresh_token,
        });
      } catch (sessionError) {
        console.warn('Session setting failed (continuing anyway):', sessionError);
      }

      const userData: User = {
        id: result.user.id,
        email: result.user.email || '',
        first_name: result.user.first_name || '',
        last_name: result.user.last_name || '',
        user_type: result.user.user_type || 'individual',
        status: 'active',
        credits: 0,
        subscription_status: 'inactive',
      };

      setUser(userData);

      // Analytics
      analytics.trackRegistration(result.user.id);
      metaPixel.trackRegistration(result.user.id);

    } catch (error: any) {
      console.error('Registration error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Logout function
  const logout = async () => {
    try {
      setIsLoading(true);
      setUser(null);
      await supabase.auth.signOut();
      analytics.trackLogout();
      console.log('✅ DEBUG logout: Logout completed successfully');
    } catch (error) {
      console.error('Logout error:', error);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  // Refresh functions (simplifiées)
  const refreshUserProfile = useCallback(async () => {
    if (!user) return;

    try {
      const profileData = await fetchProfileFromSupabase(user.id);
      setUser(prev => prev ? { ...prev, ...profileData } : null);
    } catch (error) {
      console.error('Error refreshing profile:', error);
    }
  }, [user, setUser]);

  const refreshUserCredits = async (userData?: User) => {
    const userToUpdate = userData || user;
    if (!userToUpdate) return userToUpdate;

    try {
      console.log('🔄 [AUTH] Refreshing user credits for:', userToUpdate.id);

      const profileData = await fetchProfileFromSupabase(userToUpdate.id);

      console.log('🔍 [AUTH] Profile data from Supabase:', profileData);

      const updatedUser = { ...userToUpdate, ...profileData };

      console.log('🔍 [AUTH] Updated user data:', {
        id: updatedUser.id,
        credits: updatedUser.credits,
        subscription_status: updatedUser.subscription_status,
        subscription_id: updatedUser.subscription_id,
        subscription_price_id: updatedUser.subscription_price_id,
        company_name: updatedUser.company_name,
        company_logo_url: updatedUser.company_logo_url
      });

      setUser(updatedUser);
      return updatedUser;
    } catch (error) {
      console.error('Error refreshing user credits:', error);
      return userToUpdate;
    }
  };

  // Effect pour l'initialisation - SIMPLIFIÉ
  useEffect(() => {
    let mounted = true;

    const initAuth = async () => {
      if (mounted) {
        console.log('🔍 DEBUG useEffect: Initializing auth...');
        await checkAuth();
      }
    };

    // Exécuter l'initialisation immédiatement
    initAuth();

    return () => {
      mounted = false;
    };
  }, []); // Dépendances vides pour éviter les re-exécutions

  const contextValue: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    register,
    logout,
    checkAuth,
    refreshUserProfile,
    session: null,
    refreshUserCredits
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {isLoading ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-white bg-opacity-80 backdrop-blur-sm">
          <LoadingSpinner size="lg" text="Vérification de l'authentification..." />
        </div>
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default useAuth;